// components/CleaningOverview.js
//
// Overzicht van alle schoonmaakmomenten over alle woningen van deze
// verhuurder heen (of, voor een admin, alle woningen) - zowel nog
// aankomende (open/wachtend/toegewezen) als al voltooide (voltooid/
// ingeboekt) schoonmaakbeurten, op één centrale plek i.p.v. per woning
// apart te moeten opzoeken in de Multi-kalender. Hergebruikt hetzelfde
// /api/calendar-overview endpoint als de Multi-kalender (PropertyTimeline.js),
// enkel zonder start/end-venster (dus ALLE schoonmaakbeurten, niet enkel
// een rollend venster van een paar maanden) en gefilterd op type==='cleaning'.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { getAuthToken } from '../utils/authSession';
import { cacheGet, cacheSet } from '../utils/dataCache';
import { PropertyFilter, usePropertySelection } from './PropertyFilter';
import './CleaningOverview.css';

const COMPLETED_STATUSES = ['COMPLETED', 'INVOICED'];

// De verhuurder ziet enkel twee kleuren: blauw = komt nog (Aankomend),
// groen = voltooid. De interne tussenstappen (nog niet toegewezen, wacht op
// bevestiging, toegewezen, ...) zijn enkel voor de admin relevant.
function statusClass(status) {
  if (COMPLETED_STATUSES.includes(status)) return 'completed';
  return 'upcoming';
}

// Schoonmaakoverzicht alvast ophalen bij het openen van de app, zodat de
// Schoonmaak-tab meteen gevuld is.
export const prefetchCleaningOverview = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/calendar-overview`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    const props = response.data.properties || [];
    cacheSet('cleaning-overview', {
      byId: Object.fromEntries(props.map((p) => [p.id, p.name])),
      list: props.map((p) => ({ id: p.id, name: p.name })),
      tasks: (response.data.events || []).filter((e) => e.type === 'cleaning'),
    });
  } catch (e) { /* stil */ }
};

function CleaningOverview({ showFilter = false } = {}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('fr') ? 'fr-BE' : i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL';
  const cached = cacheGet('cleaning-overview');
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [propertiesById, setPropertiesById] = useState(cached?.byId || {});
  const [propertyList, setPropertyList] = useState(cached?.list || []);
  const [tasks, setTasks] = useState(cached?.tasks || []);
  const [overviewTab, setOverviewTab] = useState('upcoming');
  const touchRef = React.useRef(null);
  const { selected, setSelected } = usePropertySelection('ss_filter_cleaning_v2', propertyList, false);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!cacheGet('cleaning-overview')) setLoading(true);
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}/api/calendar-overview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const byId = Object.fromEntries((response.data.properties || []).map((p) => [p.id, p.name]));
        setPropertiesById(byId);
        setPropertyList((response.data.properties || []).map((p) => ({ id: p.id, name: p.name })));
        setTasks((response.data.events || []).filter((e) => e.type === 'cleaning'));
        cacheSet('cleaning-overview', {
          byId,
          list: (response.data.properties || []).map((p) => ({ id: p.id, name: p.name })),
          tasks: (response.data.events || []).filter((e) => e.type === 'cleaning'),
        });
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || t('cleaningOverview.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [t]);

  const formatDateTime = (d) => new Date(d).toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  if (loading) {
    return <div className="cleaning-overview"><p className="cleaning-overview-loading" aria-busy="true" /></div>;
  }
  if (error) {
    return <div className="cleaning-overview"><p className="cleaning-overview-error">{error}</p></div>;
  }

  // Zelfde referentiemoment als hieronder in renderRow (effectieve
  // aankomsttijd indien gekend, anders het geplande startuur) - zonder
  // limiet bleef deze lijst voor een lang draaiende woning eindeloos
  // doorgroeien. 7 dagen is voldoende om terug te kijken zonder dat de
  // lijst het scherm domineert; zie ook de "(laatste 7 dagen)" hint naast
  // de titel hieronder.
  const sevenDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const completedReferenceDate = (task) => new Date(task.cleanerCheckedInAt || task.cleanerClockInAt || task.startDate);

  const visibleTasks = !showFilter ? tasks : tasks.filter((task) => selected.includes(task.propertyId));
  const upcoming = visibleTasks
    .filter((task) => !COMPLETED_STATUSES.includes(task.status))
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const completed = visibleTasks
    .filter((task) => COMPLETED_STATUSES.includes(task.status) && completedReferenceDate(task) >= sevenDaysAgo)
    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

  // Groepeert per datum (kop "maandag 12 oktober") i.p.v. 1 lange rij-lijst.
  const dateKeyOf = (task, isCompleted) => {
    const d = new Date(isCompleted ? completedReferenceDate(task) : task.startDate);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const formatDateHeading = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    const text = new Date(y, m - 1, d).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    return text.charAt(0).toUpperCase() + text.slice(1);
  };
  const renderGrouped = (list, isCompleted) => {
    const groups = [];
    list.forEach((task) => {
      const key = dateKeyOf(task, isCompleted);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.tasks.push(task);
      else groups.push({ key, tasks: [task] });
    });
    // In de app: binnen dezelfde dag alfabetisch op woning, en de schoonmakers
    // per rij alfabetisch.
    if (showFilter) {
      groups.forEach((g) => g.tasks.sort((x, y) => (propertiesById[x.propertyId] || '').localeCompare(propertiesById[y.propertyId] || '', locale, { sensitivity: 'base' })));
    }
    return groups.map((g) => (
      <div className="cleaning-overview-day" key={g.key}>
        <h4 className="cleaning-overview-day-heading">{formatDateHeading(g.key)}</h4>
        <ul className="cleaning-overview-list">{g.tasks.map((task) => renderRow(task, isCompleted))}</ul>
      </div>
    ));
  };

  const renderRow = (task, isCompleted) => {
    // Bij voltooide taken tonen we het echte moment waarop de schoonmaker
    // effectief is toegekomen (automatisch via Nuki gedetecteerd, of anders
    // handmatig ingeklokt) i.p.v. het louter geplande startuur - bij taken die
    // nog moeten gebeuren bestaat dat moment natuurlijk nog niet, dus daar
    // blijft het geplande startuur staan.
    const displayTime = isCompleted
      ? (task.cleanerCheckedInAt || task.cleanerClockInAt || task.startDate)
      : task.startDate;
    // Schoonmakernaam/-namen worden, indien gekend, gewoon achter de datum
    // getoond i.p.v. als eigen kolom/veld - op mobiel (waar de rij stapelt
    // i.p.v. naast elkaar staat) gaf een lege plaatshouder ("/") anders een
    // eigen, vrij zinloze regel op zich. Bij co-schoonmakers (cleaner_name2/3)
    // tonen we alle toegewezen namen i.p.v. enkel de eerste.
    const cleanerNameList = [task.cleaner_name, task.cleaner2_name, task.cleaner3_name].filter(Boolean);
    cleanerNameList.sort((x, y) => x.localeCompare(y, locale, { sensitivity: 'base' }));
    const cleanerNames = cleanerNameList.join(', ');
    const timeText = new Date(displayTime).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    void timeText;
    const metaText = cleanerNames;
    // "row-main" bundelt bolletje+naam in 1 sub-element (op mobiel een eigen
    // flexrij, op desktop enkel "display:contents" zodat de kinderen gewoon
    // rechtstreeks in het gedeelde grid blijven zitten - zie de CSS).
    //
    // Geen statusbadge (tekstlabel) meer per rij, noch bij "Aankomend"
    // ("Ingepland"/"Toegewezen") noch bij "Voltooid": het gekleurde bolletje
    // (rood/oranje/groen) maakt de status al duidelijk, en de sectiekop
    // ("Aankomend"/"Voltooid") geeft al aan waar we naar kijken - een los
    // tekstlabel per rij was overbodige herhaling én de bron van een
    // terugkerende uitlijningsbug (badges met wisselende breedte duwden de
    // rest van de rij scheef).
    return (
      <li className={`cleaning-overview-row row-${statusClass(task.status)}`} key={task.id}>
        <span className="cleaning-overview-row-main">
          <span className={`cleaning-overview-status-dot ${statusClass(task.status)}`}></span>
          <span className="cleaning-overview-property">{propertiesById[task.propertyId] || '—'}</span>
        </span>
        <span className="cleaning-overview-dates">{metaText}</span>
      </li>
    );
  };

  return (
    <div className="cleaning-overview">
      <div className="cleaning-overview-header">
        <h2>{t('cleaningOverview.title')}</h2>
      </div>

      {showFilter && <PropertyFilter properties={propertyList} selected={selected} onChange={setSelected} />}

      <div className="cleaning-overview-tabs">
        <button type="button" className={overviewTab === 'upcoming' ? 'active' : ''} onClick={() => setOverviewTab('upcoming')}>{t('cleaningOverview.upcomingTitle')}</button>
        <button type="button" className={overviewTab === 'completed' ? 'active' : ''} onClick={() => setOverviewTab('completed')}>{(() => { const full = t('cleaningOverview.completedTitle'); const i = full.indexOf('('); return i > 0 ? (<><span>{full.slice(0, i).trim()}</span><small className="co-tab-sub">{full.slice(i)}</small></>) : full; })()}</button>
      </div>

      <div
        className="cleaning-overview-columns"
        onTouchStart={(e) => { touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        onTouchEnd={(e) => {
          const st = touchRef.current; touchRef.current = null;
          if (!st) return;
          const dx = e.changedTouches[0].clientX - st.x;
          const dy = e.changedTouches[0].clientY - st.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) setOverviewTab(dx < 0 ? 'completed' : 'upcoming');
        }}
      >
        <div className={`cleaning-overview-section ${overviewTab === 'upcoming' ? '' : 'co-inactive'}`}>
          <h3>{t('cleaningOverview.upcomingTitle')}</h3>
          {upcoming.length === 0 ? (
            <p className="cleaning-overview-empty">{t('cleaningOverview.noUpcoming')}</p>
          ) : (
            renderGrouped(upcoming, false)
          )}
        </div>

        <div className={`cleaning-overview-section ${overviewTab === 'completed' ? '' : 'co-inactive'}`}>
          <h3>{t('cleaningOverview.completedTitle')}</h3>
          {completed.length === 0 ? (
            <p className="cleaning-overview-empty">{t('cleaningOverview.noCompleted')}</p>
          ) : (
            renderGrouped(completed, true)
          )}
        </div>
      </div>
    </div>
  );
}

export default CleaningOverview;
